interface MeasurementResult {
  hasOverflow: boolean;
  singleLineHeight: number;
  lineCount: number;
}

/**
 * Measure whether tags overflow beyond a single line and calculate row height.
 * Measures only the tags container, excluding header, tooltip, buttons.
 */
export const measureTagsOverflow = (
  tagsContainerRef: React.RefObject<HTMLDivElement>,
  measurementRef: React.RefObject<HTMLDivElement>,
  singleLineHeight = 24,
  maxVisibleLines = 1,
  overflowTolerance = 2
): MeasurementResult => {
  const tagsContainer = tagsContainerRef.current;
  const measurement = measurementRef.current;

  if (!tagsContainer || !measurement) {
    return {
      hasOverflow: false,
      singleLineHeight: 0,
      lineCount: 0,
    };
  }

  // Clone tags into hidden measurement area
  const clonedTags = tagsContainer.cloneNode(true) as HTMLDivElement;
  Object.assign(clonedTags.style, {
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'visible',
    maxHeight: 'none',
    height: 'auto',
  });

  measurement.innerHTML = '';
  measurement.appendChild(clonedTags);

  Object.assign(measurement.style, {
    display: 'flex',
    flexWrap: 'wrap',
    visibility: 'hidden',
    pointerEvents: 'none',
    width: `${tagsContainer.offsetWidth}px`,
  });

  const allTagsHeight = measurement.scrollHeight;

  const firstTagGroup = clonedTags.querySelector(
    '[class*="__tagGroup"]'
  ) as HTMLElement | null;

  if (firstTagGroup) {
    measurement.innerHTML = '';
    measurement.appendChild(firstTagGroup.cloneNode(true));

    singleLineHeight = measurement.scrollHeight || singleLineHeight;
  }

  // Cleanup
  measurement.innerHTML = '';

  // Calculate actual line count
  const lineCount = Math.max(
    1,
    Math.round((allTagsHeight - overflowTolerance) / singleLineHeight)
  );

  return {
    hasOverflow: lineCount > maxVisibleLines,
    singleLineHeight,
    lineCount,
  };
};
